import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AdminCategoriesResponse,
  MeResponse,
  OutstandingWeeksResponse,
  PostLineRequest,
  PostLineResponse,
  PutEntryResponse,
  TimesheetResponse,
  TimesheetStatusResponse,
  WeekStatusResponse,
} from '@time-tracking/shared';

import { useAuth } from '../auth/AuthContext.js';
import { todayMondayIso } from '../utils/weekDates.js';
import { ApiError, parseJson } from './client.js';
import { useApiFetch } from './useApiFetch.js';

// Re-exported for existing consumers (e.g. Timesheet.tsx) that import ApiError from hooks.
export { ApiError };

export function useMe() {
  const fetch = useApiFetch();
  const { idToken } = useAuth();
  return useQuery<MeResponse>({
    queryKey: ['me'],
    queryFn: () => fetch('/v1/me').then((r) => parseJson<MeResponse>(r)),
    enabled: Boolean(idToken),
    // Override the global `retry: 1`. A 4xx here is a verdict, not a blip — retrying the
    // unprovisioned-user 403 just delays the card that explains it. 5xx still gets one retry.
    retry: (failureCount, error) =>
      error instanceof ApiError && error.status >= 400 && error.status < 500
        ? false
        : failureCount < 1,
  });
}

export function useAdminCategories() {
  const fetch = useApiFetch();
  return useQuery<AdminCategoriesResponse>({
    queryKey: ['admin-categories'],
    queryFn: () => fetch('/v1/admin-categories').then((r) => parseJson<AdminCategoriesResponse>(r)),
  });
}

// Each week is cached independently under ['timesheet', weekStartDate]. The
// weekStartDate is always a canonical Monday string (normalised by the caller),
// so a deep link and a navigation to the same week share one cache entry.
//
// This hits GET /timesheets/current, which get-or-creates the week. `enabled`
// lets the caller defer that (and its create side effect) — the Timesheet page
// holds the current-week fetch until the last-week landing decision settles, so
// a landing that redirects to last week never creates a current-week draft.
export function useTimesheet(weekStartDate: string, options?: { enabled?: boolean }) {
  const fetch = useApiFetch();
  return useQuery<TimesheetResponse>({
    queryKey: ['timesheet', weekStartDate],
    queryFn: () =>
      fetch(`/v1/timesheets/current?weekStartDate=${encodeURIComponent(weekStartDate)}`).then((r) =>
        parseJson<TimesheetResponse>(r),
      ),
    enabled: options?.enabled ?? true,
  });
}

// Read-only status of a single week — hits GET /timesheets/status, which never
// get-or-creates (unlike useTimesheet). Used by the last-week landing check so
// deciding where to land never materialises a draft or writes an audit event.
// `enabled` gates the speculative fetch (only probe when the user hasn't picked
// a week). `retry: false` so a failed speculative check falls back to the
// current week immediately rather than holding the landing skeleton through
// retry/backoff.
//
// This is a point-in-time landing check, so it must NOT refetch on window
// focus or network reconnect. The probe stays mounted+enabled the whole time a
// contributor is on bare `/timesheet`, and the landing gate treats a probe
// `isFetching` as "week undecided" (skeleton up, current-week query deferred).
// Left on react-query defaults, a focus/reconnect refetch after staleTime would
// blank the page and interrupt someone mid-edit. staleTime:Infinity + no
// focus/reconnect refetch means it fetches once and stays put.
export function useWeekStatus(weekStartDate: string, options?: { enabled?: boolean }) {
  const fetch = useApiFetch();
  return useQuery<WeekStatusResponse>({
    queryKey: ['week-status', weekStartDate],
    queryFn: () =>
      fetch(`/v1/timesheets/status?weekStartDate=${encodeURIComponent(weekStartDate)}`).then((r) =>
        parseJson<WeekStatusResponse>(r),
      ),
    enabled: options?.enabled ?? true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: Infinity,
    retry: false,
  });
}

// The set of past weeks that still need the caller's action, powering the
// backlog badge on the week-back arrow. Scoped to the caller server-side, so
// there are no query parameters.
//
// CAUTION — the caching posture here deliberately diverges from useWeekStatus
// directly above, and the two must NOT be "made consistent". useWeekStatus is a
// one-shot landing probe: a point-in-time decision about where to land, which
// is why it pins staleTime:Infinity and switches off focus/reconnect refetch.
// This hook is a persistent indicator that has to keep telling the truth for as
// long as the tab is open, so both of those choices invert:
//
//   * No staleTime:Infinity, and focus refetch left on react-query defaults.
//     An approver can return a sheet while the contributor sits on the page;
//     with a frozen cache that week would never appear in the badge until a
//     full reload. Refetching when the tab regains focus is exactly when a
//     contributor comes back to find out what changed.
//   * The current Monday is part of the query key. A session left open across
//     Monday 00:00 America/Chicago would otherwise keep serving a set computed
//     against the old week boundary while the rest of the page's date maths
//     has already rolled forward — the key rotates, so the set recomputes.
//
// The focus refetch is only safe because the page gates pulse replay per
// newly-seen week (rather than on any data change), so a refetch that returns
// the same set cannot restart the attention animation. Preserve that gate if
// you touch this.
//
// retry: false — a decorative badge should fail quietly rather than
// retry-storm; the page renders no nudge at all while this is pending or
// errored, and never gates the timesheet on it.
export function useOutstandingWeeks() {
  const fetch = useApiFetch();
  return useQuery<OutstandingWeeksResponse>({
    queryKey: ['outstanding-weeks', todayMondayIso()],
    queryFn: () =>
      fetch('/v1/timesheets/outstanding-weeks').then((r) => parseJson<OutstandingWeeksResponse>(r)),
    retry: false,
  });
}

export function useAddLine() {
  const fetch = useApiFetch();
  const queryClient = useQueryClient();

  return useMutation<PostLineResponse, Error, PostLineRequest>({
    mutationFn: (data) =>
      fetch('/v1/timesheets/current/lines', {
        method: 'POST',
        body: JSON.stringify(data),
      }).then((r) => parseJson<PostLineResponse>(r)),
    onSuccess: () => {
      // post-line targets the current week server-side; invalidate every cached
      // week so whichever one is showing the current week refreshes.
      void queryClient.invalidateQueries({ queryKey: ['timesheet'] });
    },
  });
}

// The PUT entry, PATCH description, submit, and recall hooks all act on a single
// week. The caller passes the active weekStartDate so we invalidate only that
// week's cache entry, leaving other cached weeks untouched.
export function useUpdateEntry(weekStartDate: string) {
  const fetch = useApiFetch();
  const queryClient = useQueryClient();

  return useMutation<PutEntryResponse | null, Error, { lineId: string; totalMinutes: number }>({
    mutationFn: ({ lineId, totalMinutes }) =>
      fetch(`/v1/timesheets/current/lines/${lineId}/entry`, {
        method: 'PUT',
        body: JSON.stringify({ totalMinutes }),
      }).then((r) => {
        if (r.status === 204) return null;
        return parseJson<PutEntryResponse>(r);
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['timesheet', weekStartDate] });
    },
  });
}

export function useSubmitTimesheet(weekStartDate: string) {
  const fetch = useApiFetch();
  const queryClient = useQueryClient();

  return useMutation<TimesheetStatusResponse, ApiError, { comment: string | null }>({
    mutationFn: ({ comment }) => {
      return fetch(
        `/v1/timesheets/current/submit?weekStartDate=${encodeURIComponent(weekStartDate)}`,
        {
          method: 'POST',
          ...(comment !== null ? { body: JSON.stringify({ comment }) } : {}),
        },
      ).then((r) => parseJson<TimesheetStatusResponse>(r));
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['timesheet', weekStartDate] });
      // Submitting changes what an approver sees (including the sheet comment), so
      // drop the cached inbox — otherwise a same-session approver view serves the
      // stale note within the 30s staleTime window.
      void queryClient.invalidateQueries({ queryKey: ['approvals'] });
      // Submitting a backlog week clears it from the outstanding set — the single
      // most important moment for the badge to move. Invalidate the ['outstanding-weeks']
      // prefix rather than the fully-qualified key so it matches whichever Monday
      // the key currently carries.
      void queryClient.invalidateQueries({ queryKey: ['outstanding-weeks'] });
    },
  });
}

export function useRecallTimesheet(weekStartDate: string) {
  const fetch = useApiFetch();
  const queryClient = useQueryClient();

  return useMutation<TimesheetStatusResponse, ApiError, undefined>({
    mutationFn: () =>
      fetch(`/v1/timesheets/current/recall?weekStartDate=${encodeURIComponent(weekStartDate)}`, {
        method: 'POST',
      }).then((r) => parseJson<TimesheetStatusResponse>(r)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['timesheet', weekStartDate] });
      // A recalled sheet drops out of the approver inbox — invalidate it too.
      void queryClient.invalidateQueries({ queryKey: ['approvals'] });
      // A recall puts the week back into the outstanding set (it is a draft again),
      // so the badge has to grow back. Prefix invalidation, as in useSubmitTimesheet.
      void queryClient.invalidateQueries({ queryKey: ['outstanding-weeks'] });
    },
  });
}

export function usePatchLineDescription(weekStartDate: string) {
  const fetch = useApiFetch();
  const queryClient = useQueryClient();

  return useMutation<null, Error, { lineId: string; description: string | null }>({
    mutationFn: ({ lineId, description }) =>
      fetch(`/v1/timesheets/current/lines/${lineId}`, {
        method: 'PATCH',
        body: JSON.stringify({ description }),
      }).then((r) => {
        if (r.status === 204) return null;
        return parseJson<never>(r);
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['timesheet', weekStartDate] });
    },
  });
}
